// @file helper functions related to posts/wishes

import { getHyphenatedCurrentLocale } from '@@/bits/current_locale'
import { __ } from '@@/bits/intl'
import type { AttachmentProps, PostAttributes, WishAttachment } from '@@/types'
import type { Tweet, TweetDisplayAttributes } from '@@/types/surface'

export function isTweet(attachmentProps: AttachmentProps | null | undefined): boolean {
  return attachmentProps?.type?.toLowerCase() === 'tweet'
}

export function isArcade(attachmentProps: AttachmentProps | null | undefined): boolean {
  return attachmentProps?.type?.toLowerCase() === 'arcade'
}

export function getTweet(attachmentProps: AttachmentProps | null | undefined): Tweet {
  return {
    canDisplay: attachmentProps?.can_display ?? true,
    text: attachmentProps?.text,
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    author: attachmentProps?.author?.name || attachmentProps?.author?.username,
    createdAt: attachmentProps?.created_at,
    attachments: attachmentProps?.attachments,
    entities: attachmentProps?.entities,
  }
}

export function getTweetDisplayAttributes(
  tweet: Tweet,
  options: { monthFormat: 'short' | 'long' } = { monthFormat: 'long' },
): TweetDisplayAttributes {
  const canDisplay = tweet.canDisplay
  if (!canDisplay) {
    return {
      text: __('Tweet cannot be displayed'),
      author: tweet.author,
    }
  }

  const date = new Date(tweet.createdAt)
  const createdAtDateString = date.toLocaleString(getHyphenatedCurrentLocale(), {
    year: 'numeric',
    month: options.monthFormat,
    day: 'numeric',
  })
  return {
    text: tweet.text,
    author: tweet.author,
    createdAtDateString,
  }
}

export function isPhotoAlbum(post?: PostAttributes | null): post is PostAttributes & { attachments: WishAttachment[] } {
  return post?.attachments != null && post.attachments.length > 1
}

export function hasRegularAttachment(post?: PostAttributes | null): boolean {
  return !!post?.attachment
}

export function hasRichAttachment(post?: PostAttributes | null): boolean {
  return post?.wish_content?.attachment_props?.type != null
}

export function hasAnyAttachments(post?: PostAttributes | null): boolean {
  return hasRegularAttachment(post) || hasRichAttachment(post) || isPhotoAlbum(post)
}

export type PostInteraction = 'expand' | 'link' | 'download' | 'padlet' | 'play'

const COMMON_IMAGE_FILE_EXTENSIONS = [
  // Common image file types based on MDN Web Docs
  // https://developer.mozilla.org/en-US/docs/Web/Media/Guides/Formats/Image_types#common_image_file_types
  '.apng',
  '.avif',
  '.gif',
  '.jpg',
  '.jpeg',
  '.jfif',
  '.pjpeg',
  '.pjp',
  '.png',
  '.svg',
  '.webp',
  // iOS
  '.heic',
  '.heif',
] as const

/**
 * Checks if a file has a common image file extension
 * @param file - The file to check
 * @returns true if the file has an image extension, false otherwise
 */
export function isFileWithImageFileExtension(file: File): boolean {
  const fileName = file.name.toLowerCase()
  return COMMON_IMAGE_FILE_EXTENSIONS.some((ext) => fileName.endsWith(ext))
}
